/**
 * Real-adapter surface for CSR account allocation
 * (docs/features/csr-account-allocation/). Reads hit views_module's
 * composed `/views/account-allocation/*` + `/views/admin/possible-leavers`
 * endpoints (spec 06); writes hit `account_allocation_module`'s
 * `/account-allocation/*` routes (spec 05) and `users_module`'s
 * mark-left/reinstate/dismiss routes (spec 04).
 *
 * The view-module convention expresses every entity's own id as a single
 * `id` field. This module adds the domain-named convenience fields
 * (`customer_id`, `claim_id`, `user_id`, `reallocation_id`) the pages were
 * built against — see the header comment on `../account-allocation-types.ts`.
 */

import type {
  AllocationClaimListOptions,
  AllocationClaimRow,
  AllocationSummary,
  BulkReallocationPreview,
  BulkReallocationPreviewQuery,
  BulkReallocationRequest,
  BulkReallocationResult,
  ClaimResult,
  PossibleLeaverListOptions,
  PossibleLeaverRow,
  PossibleLeaverSignal,
  ReallocationQueueListOptions,
  ReallocationQueueRow,
  ReallocationResult,
} from "../account-allocation-types";
import type {
  AllocationClaimItemResponse,
  BulkReallocateResponse,
  ClaimResponse,
  PossibleLeaverItemResponse,
  ReallocationQueueItemResponse,
  ReallocationResponse,
} from "../api-types";
import type { ListResponse } from "../adapter";
import { apiClient } from "./client";
import { ApiError } from "./errors";
import { extractFieldErrors } from "../validation";

function toListParams(base: Record<string, string | undefined>) {
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(base)) {
    if (v !== undefined) params[k] = v;
  }
  return params;
}

interface RawListResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

function fromRawList<T, U>(
  raw: RawListResponse<T>,
  mapItem: (item: T) => U,
): ListResponse<U> {
  return {
    items: raw.items.map(mapItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
    totalPages: raw.total_pages,
  };
}

function toQueueRow(r: ReallocationQueueItemResponse): ReallocationQueueRow {
  return {
    ...r,
    customer_id: r.id,
    reason: (r.reason as ReallocationQueueRow["reason"]) ?? null,
    days_since_last_order: r.days_since_last_order ?? null,
    jobs_12m: r.jobs_12m ?? null,
    last_ordered: r.last_ordered ?? null,
    suggestion: r.suggestion
      ? { ...r.suggestion, evidence: r.suggestion.evidence ?? [] }
      : null,
  };
}

function toClaimRow(r: AllocationClaimItemResponse): AllocationClaimRow {
  return { ...r, claim_id: r.id };
}

function toPossibleLeaverRow(r: PossibleLeaverItemResponse): PossibleLeaverRow {
  return { ...r, user_id: r.id, user_name: r.name };
}

function toReallocationResult(r: ReallocationResponse): ReallocationResult {
  return { ...r, reallocation_id: r.id };
}

export async function getAccountAllocationQueue(
  options?: ReallocationQueueListOptions,
): Promise<ListResponse<ReallocationQueueRow>> {
  const params = toListParams({
    page: options?.page ? String(options.page) : undefined,
    page_size: options?.pageSize ? String(options.pageSize) : undefined,
    sort: options?.sort,
    q: options?.q,
    suggested_user_id: options?.suggestedUserId,
    claim_state: options?.claimState,
    has_awaiting_reallocation:
      options?.hasAwaitingReallocation !== undefined
        ? String(options.hasAwaitingReallocation)
        : undefined,
  });
  const qs = new URLSearchParams(params).toString();
  const raw = await apiClient.get<
    RawListResponse<ReallocationQueueItemResponse>
  >(`views/account-allocation/queue${qs ? `?${qs}` : ""}`);
  return fromRawList(raw, toQueueRow);
}

export async function getBulkReallocationPreview(
  query: BulkReallocationPreviewQuery,
): Promise<BulkReallocationPreview> {
  const params = toListParams({
    q: query.q,
    suggested_user_id: query.suggestedUserId,
    claim_state: query.claimState,
    has_awaiting_reallocation:
      query.hasAwaitingReallocation !== undefined
        ? String(query.hasAwaitingReallocation)
        : undefined,
    target_user_id: query.targetUserId,
  });
  const search = new URLSearchParams(params);
  // The ticked rows — matches the backend's `customer_ids: list[str]`
  // query field (spec 06 §9/§11); FastAPI binds a repeated query param
  // named after the field itself, so every value is appended under
  // `customer_ids`, not a singular key.
  for (const id of query.customerIds ?? []) search.append("customer_ids", id);
  const qs = search.toString();
  return apiClient.get<BulkReallocationPreview>(
    `views/account-allocation/bulk-preview${qs ? `?${qs}` : ""}`,
  );
}

export async function getAllocationClaims(
  options?: AllocationClaimListOptions,
): Promise<ListResponse<AllocationClaimRow>> {
  const params = toListParams({
    page: options?.page ? String(options.page) : undefined,
    page_size: options?.pageSize ? String(options.pageSize) : undefined,
    sort: options?.sort,
    claimant_user_id: options?.claimantUserId,
  });
  const search = new URLSearchParams(params);
  for (const s of options?.status ?? []) search.append("status", s);
  const qs = search.toString();
  const raw = await apiClient.get<RawListResponse<AllocationClaimItemResponse>>(
    `views/account-allocation/claims${qs ? `?${qs}` : ""}`,
  );
  return fromRawList(raw, toClaimRow);
}

export async function getAllocationSummary(): Promise<AllocationSummary> {
  return apiClient.get<AllocationSummary>("views/account-allocation/summary");
}

export async function getPossibleLeavers(
  options?: PossibleLeaverListOptions,
): Promise<ListResponse<PossibleLeaverRow>> {
  const params = toListParams({
    page: options?.page ? String(options.page) : undefined,
    page_size: options?.pageSize ? String(options.pageSize) : undefined,
    sort: options?.sort,
    signal: options?.signal,
    include_dismissed:
      options?.includeDismissed !== undefined
        ? String(options.includeDismissed)
        : undefined,
  });
  const qs = new URLSearchParams(params).toString();
  const raw = await apiClient.get<RawListResponse<PossibleLeaverItemResponse>>(
    `views/admin/possible-leavers${qs ? `?${qs}` : ""}`,
  );
  return fromRawList(raw, toPossibleLeaverRow);
}

export async function reallocateCustomer(
  customerId: string,
  toUserId: string,
): Promise<ReallocationResult> {
  const result = await apiClient.post<ReallocationResponse>(
    "account-allocation/reallocations",
    {
      customer_id: customerId,
      to_user_id: toUserId,
    },
  );
  return toReallocationResult(result);
}

export async function bulkReallocate(
  request: BulkReallocationRequest,
): Promise<BulkReallocationResult> {
  return apiClient.post<BulkReallocateResponse>(
    "account-allocation/reallocations/bulk",
    request,
  );
}

export async function retryFailedReallocation(
  reallocationId: string,
): Promise<ReallocationResult> {
  const result = await apiClient.post<ReallocationResponse>(
    `account-allocation/reallocations/${reallocationId}/retry`,
    {},
  );
  return toReallocationResult(result);
}

/**
 * `ClaimResponse` (account_allocation_module exposes no reads of its own)
 * carries no `customer_name`/`claimant_name` — hydrate the display-ready
 * `AllocationClaimRow` by refetching the claimant's claims list (the same
 * composed read the claims tab and the customer page use), falling back to
 * a row built from the write response if the list doesn't have it yet (a
 * conceivable read-after-write race, not expected in practice).
 */
async function hydrateClaimRow(
  claim: ClaimResponse,
): Promise<AllocationClaimRow> {
  const { items } = await getAllocationClaims({
    claimantUserId: claim.claimant_user_id,
    pageSize: 100,
  });
  const hydrated = items.find((c) => c.claim_id === claim.id);
  if (hydrated) return hydrated;
  return {
    ...claim,
    claim_id: claim.id,
    type: "allocation_claim",
    _self: `/views/customer-360/${claim.customer_id}`,
    customer_name: claim.customer_id,
    claimant_name: claim.claimant_user_id,
    decided_by_name: claim.decided_by_user_id ?? null,
  };
}

export async function claimCustomer(customerId: string): Promise<ClaimResult> {
  const claim = await apiClient.post<ClaimResponse>(
    "account-allocation/claims",
    { customer_id: customerId },
  );
  return { claim: await hydrateClaimRow(claim) };
}

export async function approveClaim(
  claimId: string,
): Promise<ReallocationResult> {
  const result = await apiClient.post<ReallocationResponse>(
    `account-allocation/claims/${claimId}/approve`,
    {},
  );
  return toReallocationResult(result);
}

export async function declineClaim(
  claimId: string,
  note?: string,
): Promise<ClaimResult> {
  const claim = await apiClient.post<ClaimResponse>(
    `account-allocation/claims/${claimId}/decline`,
    { note },
  );
  return { claim: await hydrateClaimRow(claim) };
}

export async function withdrawClaim(claimId: string): Promise<ClaimResult> {
  const claim = await apiClient.post<ClaimResponse>(
    `account-allocation/claims/${claimId}/withdraw`,
    {},
  );
  return { claim: await hydrateClaimRow(claim) };
}

export async function markUserLeft(userId: string, leftOn: string) {
  return apiClient.post(`users/${userId}/mark-left`, { left_on: leftOn });
}

export async function reinstateUser(userId: string) {
  return apiClient.post(`users/${userId}/reinstate`, {});
}

export async function dismissPossibleLeaver(
  userId: string,
  signal: PossibleLeaverSignal,
) {
  await apiClient.post(`users/possible-leavers/${userId}/dismiss`, {
    signal,
  });
}

/**
 * Second line of copy for a failed Tharstern write, keyed on the backend's
 * `failure_reason_code` (account_allocation_module bootstrap:
 * customer_not_found / unreachable / write_rejected). Unknown or missing
 * codes fall back rather than hiding that no reason was given.
 */
export function tharsternFailureReasonText(
  code: string | null | undefined,
): string {
  switch (code) {
    case "customer_not_found":
      return "Tharstern couldn't find this customer";
    case "unreachable":
      return "Tharstern couldn't be reached";
    case "write_rejected":
      return "Tharstern rejected the update";
    default:
      return "Tharstern gave no reason";
  }
}

/**
 * The single place that turns an account-allocation write failure into
 * display copy. Real backend errors come in two shapes: the canonical 400
 * field-error envelope (api-validation.md — `target_not_eligible`/
 * `name_ambiguous`/`name_missing`) and a plain string `.detail` for
 * 409 (precondition: not in queue / claim already pending / self-approval
 * / not claimant) and 502 (Tharstern write rejected). Pages call this
 * instead of reading `.detail` or `.body` themselves.
 */
export function accountAllocationErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return "Tharstern didn't accept the change — try again";
  }
  if (err.status === 502) {
    return "Tharstern didn't accept the change — try again";
  }
  const fieldErrors = extractFieldErrors(err);
  if (fieldErrors.length > 0) {
    const code = fieldErrors[0].code;
    if (code === "name_ambiguous" || code === "name_missing") {
      return "Can't assign until the Tharstern user is fixed — this CSR's Tharstern name doesn't resolve";
    }
    return fieldErrors[0].message;
  }
  return err.detail || "Tharstern didn't accept the change — try again";
}
